import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { RootState } from '../store'
import { arraysEqual, uniqueArray } from '../utilities'

type TaskDB = Record<string, LocalTask>

type FormatFunc = (task: any) => LocalTask

type UpdateAction = {
  task: LocalTask
  replaceTemp?: boolean
  updateSubCount?: boolean
  onSubCountDiffer?: () => void
  endLoad?: boolean
}

type TasksState = {
  tasks: TaskDB
  loaded: boolean
  loading: string[]
  blockLoad: string[]
  subQueue: string[]
  updatedTask: Partial<APITask>
}

const initialState: TasksState = {
  tasks: {},
  loaded: false,
  loading: [],
  blockLoad: [],
  subQueue: [],
  updatedTask: {}
}

const tasksSlice = createSlice({
  name: 'tasks',
  initialState,
  reducers: {
    finishInitialLoad: (
      state: TasksState,
      action: PayloadAction<LocalTask[]>
    ) => {
      const tasks: TaskDB = {}
      action.payload.forEach(task => {
        tasks[task.id] = task
      })
      state.tasks = tasks
      state.loaded = true
    },

    update: (state: TasksState, action: PayloadAction<UpdateAction>) => {
      const {
        task,
        replaceTemp,
        updateSubCount,
        onSubCountDiffer,
        endLoad = false
      } = action.payload

      if (endLoad) {
        state.loading = state.loading.filter(loadingID => loadingID !== task.id)
      }

      if (replaceTemp) {
        const newTasks: TaskDB = {}

        Object.entries(state.tasks).forEach(([id, val]) => {
          if (id !== 'temp') {
            newTasks[id] = val
          }
        })

        newTasks[task.id] = task
        state.tasks = newTasks
        return
      }

      const newTask = { ...task }
      const oldTask = state.tasks[task.id]

      // Notify caller that sub_tasks need updating
      if (onSubCountDiffer) {
        if (oldTask && newTask.sub_tasks !== oldTask.sub_tasks) {
          onSubCountDiffer()
        }
      }

      if (!updateSubCount) {
        newTask.sub_tasks = oldTask?.sub_tasks ?? 0
        newTask.subtasks = oldTask?.subtasks ?? []
      }

      state.tasks[task.id] = newTask
    },

    deleteTask: (state: TasksState, action: PayloadAction<string>) => {
      const id = action.payload

      const newTasks: TaskDB = {}
      Object.entries(state.tasks).forEach(([key, val]) => {
        if (key !== id) {
          newTasks[key] = { ...val }
        }
      })

      const parentID = state.tasks[id]?.parent_id
      if (parentID) {
        const parent = newTasks[parentID]
        const newSubCount = (parent?.sub_tasks ?? 0) - 1
        newTasks[parentID] = {
          ...parent,
          sub_tasks: Math.max(newSubCount, 0),
          subtasks: parent?.subtasks?.filter(t => t !== id)
        }
      }

      state.tasks = newTasks
    },

    propagateMerge: (
      state: TasksState,
      action: PayloadAction<{ from: string; to: string }>
    ) => {
      const { from, to } = action.payload
      const replaceOne = (id: string) => (id === from ? to : id)

      const newTasks: TaskDB = {}
      Object.entries(state.tasks).forEach(([id, task]) => {
        const newTask: LocalTask = {
          ...task,
          assignee_ids: (task.assignee_ids ?? []).map(replaceOne),
          concerning_user_id: replaceOne(task.concerning_user_id),
          created_by: replaceOne(task.created_by),
          primary_assignee_id: replaceOne(task.primary_assignee_id),
          watcher_ids: (task.watcher_ids ?? []).map(replaceOne)
        }

        newTasks[id] = newTask
      })

      state.tasks = newTasks
    },

    updateFormat: (state: TasksState, action: PayloadAction<FormatFunc>) => {
      const format = action.payload
      Object.entries(state.tasks).forEach(([id, task]) => {
        state.tasks[id] = format(task)
      })
    },

    updateLoading: (state: TasksState, action: PayloadAction<string[]>) => {
      const newLoading = uniqueArray(state.loading, action.payload).filter(
        Boolean
      )

      if (arraysEqual(newLoading, state.loading)) {
        return
      }

      state.loading = newLoading
    },

    clearLoading: (state: TasksState, action: PayloadAction<string[]>) => {
      action.payload.forEach(id => {
        // Might need to change this later, possible bug
        state.tasks[id] = {} as LocalTask
      })

      state.loading = []
    },

    tasksLoaded: (
      state: TasksState,
      action: PayloadAction<{ tasks: LocalTask[]; ids: string[] }>
    ) => {
      const { tasks, ids } = action.payload

      const newTasks: TaskDB = {}
      tasks.forEach(task => {
        newTasks[task.id] = {
          ...(state.tasks[task.id] ?? {}),
          ...task
        }
      })

      const combined = { ...state.tasks, ...newTasks }

      ids.forEach(id => {
        if (Object.values(combined[id] ?? {}).length <= 2) {
          delete combined[id]
          state.blockLoad.push(id)
        }
      })

      state.tasks = combined
    },

    queueSub: (state: TasksState, action: PayloadAction<string>) => {
      const id = action.payload
      if (state.subQueue.includes(id)) {
        return
      }

      state.subQueue.push(id)
    },

    clearSub: (state: TasksState, action: PayloadAction<string>) => {
      const id = action.payload
      if (!state.subQueue.includes(id)) {
        return
      }

      state.subQueue = state.subQueue.filter(tid => tid !== id)
    },

    updateCacheSWR: (
      state: TasksState,
      action: PayloadAction<{ tasks: any[]; format: FormatFunc }>
    ) => {
      const { tasks, format } = action.payload

      tasks.forEach(task => {
        const formatted = format(task)
        const currentTask = state.tasks[formatted.id]

        // Update if the incoming task is newer or not already storec
        if (
          !currentTask ||
          new Date(formatted.updated_at) > new Date(currentTask.updated_at)
        ) {
          state.tasks[formatted.id] = formatted
        }
      })
    },

    taskUpdated: (
      state: TasksState,
      action: PayloadAction<Partial<APITask>>
    ) => {
      state.updatedTask = action.payload
    },

    patch: (state: TasksState, action: PayloadAction<TaskPatch[]>) => {
      action.payload.forEach(change => {
        const { id, ...props } = change
        if (id in state.tasks) {
          state.tasks[id] = { ...state.tasks[id], ...props }
        }
      })
    },

    setCommentCount: (
      state: TasksState,
      action: PayloadAction<{ id: string; count: number }>
    ) => {
      const { id, count } = action.payload
      state.tasks[id].comments = count
    },

    addSingle: (state: TasksState, action: PayloadAction<LocalTask>) => {
      const { id } = action.payload
      if (state.tasks[id]) {
        return
      }
      state.tasks[id] = action.payload
    }
  }
})

export const {
  finishInitialLoad,
  update,
  deleteTask,
  propagateMerge,
  updateFormat,
  updateLoading,
  clearLoading,
  tasksLoaded,
  queueSub,
  clearSub,
  updateCacheSWR,
  taskUpdated,
  patch,
  setCommentCount,
  addSingle
} = tasksSlice.actions

export const selectTasks = (state: RootState) => state.tasks

export default tasksSlice.reducer
